/**
 * Current dashboard parameter VALUES (S10) — runtime mirror of the persisted
 * `DashboardUiState.paramValues` sidecar. Definitions live on the dashboard;
 * selections live here and persist per dashboard via `setUiState` (per-key
 * deep-merge, C1 semantics), never inside the shared definition.
 */
import { create } from 'zustand';
import type { BoundParams } from '../types/dashboardTypes';

type DashboardParamState = {
  valuesByDashboard: Record<string, BoundParams>;
  /** True once persisted values are LOADED — tiles gate their first fetch on
   * this so a restored selection never causes a defaults-then-restored
   * double-run on dashboard open. */
  hydrated: Record<string, boolean>;
  hydrate: (dashboardId: string) => Promise<void>;
  /** `undefined` DELETES the selection (defaults re-apply) — mirrored in the
   * persisted sidecar via the C1 per-key merge's explicit-undefined delete. */
  setValue: (dashboardId: string, paramId: string, value: BoundParams[string] | undefined) => void;
};

const EMPTY: BoundParams = {};
const inFlight = new Map<string, Promise<void>>();

export const useDashboardParamStore = create<DashboardParamState>((set, get) => ({
  valuesByDashboard: {},
  hydrated: {},

  hydrate: async (dashboardId) => {
    if (get().hydrated[dashboardId]) return;
    const pending = inFlight.get(dashboardId);
    if (pending) return pending;
    const load = (async () => {
      try {
        const uiState = await window.electronAPI?.dashboards?.getUiState(dashboardId);
        const persisted = uiState?.paramValues;
        if (persisted && Object.keys(persisted).length > 0) {
          set((s) => ({
            valuesByDashboard: {
              ...s.valuesByDashboard,
              // Live edits made while hydration was in flight win over disk.
              [dashboardId]: { ...persisted, ...s.valuesByDashboard[dashboardId] },
            },
          }));
        }
      } catch {
        // Sidecar unavailable — defaults apply; selections just won't restore.
      } finally {
        set((s) => ({ hydrated: { ...s.hydrated, [dashboardId]: true } }));
        inFlight.delete(dashboardId);
      }
    })();
    inFlight.set(dashboardId, load);
    return load;
  },

  setValue: (dashboardId, paramId, value) => {
    set((s) => {
      const current: BoundParams = { ...(s.valuesByDashboard[dashboardId] ?? {}) };
      if (value === undefined) delete current[paramId];
      else current[paramId] = value;
      return { valuesByDashboard: { ...s.valuesByDashboard, [dashboardId]: current } };
    });
    // Fire-and-forget: per-key deep-merge means concurrent alertState/trust
    // patches from other features never clobber each other.
    window.electronAPI?.dashboards
      ?.setUiState(dashboardId, { paramValues: { [paramId]: value } })
      .catch(() => {});
  },
}));

export const selectParamValues =
  (dashboardId: string | null) =>
  (s: DashboardParamState): BoundParams =>
    (dashboardId ? s.valuesByDashboard[dashboardId] : undefined) ?? EMPTY;

export const selectParamsHydrated =
  (dashboardId: string | null) =>
  (s: DashboardParamState): boolean =>
    dashboardId ? s.hydrated[dashboardId] === true : true;
